import {
  ALL_PERMISSIONS,
  computePermissions,
  computeThreadPermissions,
  parsePermissions,
} from '@comms/shared';
import { useStore, type State } from '../store';

export function permissionsFor(
  s: State,
  guildId: string | null | undefined,
  channelId?: string | null,
): bigint {
  const me = s.me;
  if (!me || !guildId) return ALL_PERMISSIONS;
  const guild = s.guilds[guildId];
  const member = s.members[guildId]?.[me.id];
  if (!guild || !member) return 0n;
  const channel = channelId ? s.channels[channelId] : undefined;
  if (channel?.threadMetadata && channel.parentId) {
    // Threads derive everything from their parent channel.
    const parentPerms = permissionsFor(s, guildId, channel.parentId);
    return computeThreadPermissions(
      parentPerms,
      { type: channel.type, locked: channel.threadMetadata.locked },
      !!s.joinedThreads[channel.id],
    );
  }
  return computePermissions(
    {
      guildId,
      ownerId: guild.ownerId,
      userId: me.id,
      roles: guild.roles.map((r) => ({
        id: r.id,
        permissions: parsePermissions(r.permissions),
        position: r.position,
      })),
      memberRoleIds: member.roles,
      communicationDisabledUntil: member.communicationDisabledUntil
        ? new Date(member.communicationDisabledUntil)
        : null,
    },
    channel?.permissionOverwrites.map((o) => ({
      id: o.id,
      type: o.type,
      allow: parsePermissions(o.allow),
      deny: parsePermissions(o.deny),
    })),
  );
}

export function usePermissions(
  guildId: string | null | undefined,
  channelId?: string | null,
): bigint {
  return useStore((s) => permissionsFor(s, guildId, channelId));
}

export const has = (perms: bigint, flag: bigint) => (perms & flag) === flag;
